import * as React from "react";
import RouterLink from "next/link";
import type { MapPortalData } from "~mangomap-ui/utils/mock-map-portal-data";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardMedia from "@mui/material/CardMedia";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { CalendarBlank as CalendarIcon } from "@phosphor-icons/react/CalendarBlank";
import { User as CreatorIcon } from "@phosphor-icons/react/User";
import { Users } from "@phosphor-icons/react/Users";

import { AccessModeIndicator } from "../access-mode-indicator";
import { useBulkSelectionOptional } from "../bulk-selection-provider";
import { MapCardMenu } from "../map-card-menu";
import { MapAccessSettingsIndicators } from "../map-access-settings-indicators";

export interface MapCardProps {
	map: MapPortalData;
	href?: string;
	onClick?: (event: { map: MapPortalData }) => void;
	onDuplicate?: (event: { map: MapPortalData }) => void;
	onDelete?: (event: { map: MapPortalData }) => void;
	loading?: boolean;
	canDelete?: boolean;
}

export function MapCard({
	map,
	href,
	onClick,
	onDuplicate,
	onDelete,
	loading = false,
	canDelete = false,
}: MapCardProps): React.JSX.Element {
	const bulkSelection = useBulkSelectionOptional();

	// Simplified permission logic
	const canEdit = canDelete; // For now, edit and delete permissions are the same
	const showActions = canEdit || canDelete;

	const handleClick = () => {
		onClick?.({ map });
	};

	const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		event.stopPropagation();
		event.preventDefault();

		if (bulkSelection?.canSelectMap(map)) {
			bulkSelection.toggleMapSelection(map.id);
		}
	};

	const handleCheckboxKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === "Enter" || event.key === " ") {
			event.preventDefault();
			event.stopPropagation();
			if (bulkSelection?.canSelectMap(map)) {
				bulkSelection.toggleMapSelection(map.id);
			}
		}
	};

	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleDateString("en-US", {
			month: "short",
			day: "numeric",
			year: "numeric",
		});
	};

	const formatDateShort = (dateString: string) => {
		return new Date(dateString).toLocaleDateString("en-US", {
			month: "short",
			day: "numeric",
		});
	};

	const formatOwnerShort = (owner: string) => {
		return owner.split(" ")[0];
	};

	const metadataChipSx = {
		height: 24,
		fontSize: "0.75rem",
	};

	const fallbackImage = "/app-assets/map-placeholder.png";

	return (
		<Card
			sx={{
				position: "relative",
				height: "100%",
				display: "flex",
				flexDirection: "column",
				cursor: loading ? "default" : "pointer",
				transition: "all .2s ease-in-out",
				"&:hover": loading
					? {}
					: {
							transform: "translateY(-2px)",
							boxShadow: "var(--mui-shadows-8)",
							"& .selection-checkbox": {
								opacity: 1,
							},
						},
				opacity: loading ? 0.7 : 1,
				borderRadius: 2,
				bgcolor: bulkSelection?.isSelectionMode
					? "var(--mui-palette-action-hover)"
					: "transparent",
				"& .selection-checkbox": {
					opacity: bulkSelection?.isSelectionMode ? 1 : 0,
					transition: "opacity 0.2s ease-in-out",
					"&.selected": {
						opacity: 1,
					},
				},
			}}>
			{showActions && (
				<Box
					sx={{
						position: "absolute",
						top: 8,
						right: 8,
						zIndex: 3,
					}}>
					<MapCardMenu
						map={map}
						onDuplicate={onDuplicate}
						onDelete={onDelete}
						size="small"
						canDelete={canDelete}
					/>
				</Box>
			)}
			<CardActionArea
				{...(href ? { component: RouterLink, href } : { onClick: handleClick })}
				disabled={loading}
				sx={{
					height: "100%",
					display: "flex",
					flexDirection: "column",
					alignItems: "stretch",
					transform: "scale(1)",
					transition: "transform 0.1s ease-in-out",
					"&.Mui-disabled": {
						pointerEvents: "none",
					},
				}}>
				{/* Thumbnail with overlay controls */}
				<Box
					sx={{
						pt: "calc(100% * 9 / 16)",
						position: "relative",
						borderRadius: "8px 8px 0 0",
						overflow: "hidden",
					}}>
					<CardMedia
						image={map.thumbnailUrl ?? fallbackImage}
						sx={{
							height: "100%",
							left: 0,
							position: "absolute",
							top: 0,
							width: "100%",
							bgcolor: "var(--mui-palette-background-level1)",
						}}
						onError={(e) => {
							const target = e.target as HTMLElement;
							target.style.backgroundImage = `url(${fallbackImage})`;
						}}
					/>

					{/* Selection Checkbox - Top Left (when bulk selection is active) */}
					{bulkSelection && bulkSelection.canSelectMap(map) && (
						<Box
							sx={{
								position: "absolute",
								top: 8,
								left: 8,
								zIndex: 2,
							}}>
							<Checkbox
								onClick={(e) => {
									e.stopPropagation();
								}}
								checked={bulkSelection.isMapSelected(map.id)}
								onChange={handleCheckboxChange}
								onKeyDown={handleCheckboxKeyDown}
								size="small"
								className={`selection-checkbox${bulkSelection.isMapSelected(map.id) ? " selected" : ""}`}
								sx={{
									bgcolor: "rgba(255, 255, 255, 0.9)",
									backdropFilter: "blur(4px)",
									borderRadius: 1,
									"&:hover": {
										bgcolor: "rgba(255, 255, 255, 1)",
									},
									"& .MuiSvgIcon-root": {
										fontSize: "1.1rem",
									},
								}}
							/>
						</Box>
					)}
				</Box>

				{/* Content */}
				<Stack
					spacing={{ xs: 1, sm: 1.5, md: 2 }}
					sx={{ p: { xs: 1.5, sm: 2, md: 3 }, flex: 1 }}>
					{/* Title with inline actions */}
					<Stack
						direction="row"
						spacing={1}
						sx={{ alignItems: "center" }}>
						<Typography
							variant="subtitle1"
							component="h3"
							sx={{
								fontWeight: 600,
								lineHeight: 1.3,
								display: "-webkit-box",
								WebkitBoxOrient: "vertical",
								WebkitLineClamp: 2,
								overflow: "hidden",
								textOverflow: "ellipsis",
								flex: 1,
							}}>
							{map.title}
						</Typography>
					</Stack>

					{/* Description */}
					<Typography
						color="text.secondary"
						variant="body2"
						sx={{
							display: "-webkit-box",
							WebkitBoxOrient: "vertical",
							WebkitLineClamp: 2,
							overflow: "hidden",
							textOverflow: "ellipsis",
							lineHeight: 1.4,
							wordBreak: "break-word",
							maxHeight: "calc(1.4em * 2)",
							flex: 1,
						}}>
						{map.description}
					</Typography>

					{/* Metadata chips */}
					{canDelete && (
						<Stack
							direction="row"
							spacing={1}
							sx={{
								alignItems: "center",
								flexWrap: "wrap",
								gap: 1,
							}}>
							<AccessModeIndicator
								accessMode={map.privacy}
								size="small"
							/>
							<MapAccessSettingsIndicators
								accessMode={map.privacy}
								size="small"
								shownInPortal={map.shownInPortal}
								searchEngineIndexingAllowed={map.searchEngineIndexingAllowed}
							/>
							{map.isEditableByAnyGroup && (
								<Tooltip title="Editable by authorised users in enabled groups.">
									<Chip
										icon={<Users size={14} />}
										label="Group Editable"
										color="info"
										size="small"
										variant="outlined"
										sx={metadataChipSx}
									/>
								</Tooltip>
							)}
							<Tooltip
								title={`Created by ${map.createdBy} on ${formatDate(map.createdDate)}`}>
								<Chip
									icon={<CreatorIcon size={14} />}
									label={formatOwnerShort(map.createdBy)}
									size="small"
									variant="outlined"
									sx={metadataChipSx}
								/>
							</Tooltip>
							<Tooltip title={`Last updated: ${formatDate(map.lastUpdated)}`}>
								<Chip
									icon={<CalendarIcon size={14} />}
									label={formatDateShort(map.lastUpdated)}
									size="small"
									variant="outlined"
									sx={metadataChipSx}
								/>
							</Tooltip>
						</Stack>
					)}
				</Stack>
			</CardActionArea>
		</Card>
	);
}
